/**
 * blockedSenderIndex — pure resolution cache behind `isBlockedSender`.
 *
 * The inbox only ever needs to know whether the ~25–50 senders it is currently
 * rendering are blocked. It used to answer that by streaming the whole
 * `World/{uid}/blacklist` collection into the client — on a world with a bulk
 * imported blacklist that is ~28.000 documents and 18 MB over the wire, paid
 * again on every single inbox open. The cost scaled with the blacklist while
 * the benefit stayed at "draw up to 50 little x icons", and because it is a
 * pure download it hit slow connections quadratically harder than fast ones:
 * the same world took ~3 s on a 50 Mbit/s line and ~47 s on a 3 Mbit/s one.
 *
 * This module inverts the cost model. We resolve single addresses on demand
 * and remember the answers, so the price is proportional to the senders on
 * screen and drops to zero once they are known.
 *
 * The index holds two maps, both storing an explicit boolean so that "we know
 * this is not blocked" is distinguishable from "we have not asked yet":
 *   - `emails`  address        → blocked?   ("jane@acme.com")
 *   - `domains` "@" + hostname → blocked?   ("@acme.com", a domain-level block
 *                                            covers every address on it)
 *
 * Caching domains separately is what keeps a long scroll cheap: once
 * "@acme.com" is known, every further sender on that domain is free.
 */

import { extractEmail, parseAddress } from './emailParsing';

/** Mail fields that can carry the person a row displays (see MailListItem). */
const PERSON_FIELDS = ['from', 'senderEmail', 'to', 'recipientEmail', 'recipients'];

export const emptyIndex = () => ({ emails: new Map(), domains: new Map() });

/**
 * Normalizes any From/To shape (string, `Name <a@b>`, `{ name, address }`,
 * array) into a bare lowercased address.
 *
 * Must stay identical to what `isBlockedSender` receives from the row, which
 * is why both go through this one function.
 */
export const senderAddress = (from) => (
  extractEmail(from) || parseAddress(from).email || ''
).toLowerCase().trim();

/** "jane@acme.com" → "@acme.com" (the blacklist doc id for a domain block). */
export const domainKey = (address) => {
  const at = address.lastIndexOf('@');
  return at === -1 ? '' : address.slice(at);
};

/**
 * Every address the given mails could ask about, deduped.
 *
 * We collect all person-ish fields rather than replicating the row's
 * incoming/outgoing precedence: a superset costs at most one extra address per
 * mail but can never miss the one the row actually tests.
 */
export const collectSenderAddresses = (mails) => {
  if (!Array.isArray(mails) || mails.length === 0) return [];
  const seen = new Set();
  for (const mail of mails) {
    if (!mail) continue;
    for (const field of PERSON_FIELDS) {
      const value = mail[field];
      if (value == null || value === '') continue;
      const address = senderAddress(value);
      if (address) seen.add(address);
    }
  }
  return [...seen];
};

/**
 * The subset of `addresses` we still have to ask the server about.
 *
 * An address is settled once we know its own status, or once its domain is
 * known to be blocked (which already makes it blocked). A domain known to be
 * *not* blocked says nothing about the individual address, so those still go.
 */
export const pendingAddresses = (addresses, index) => {
  const out = [];
  const queued = new Set();
  for (const address of addresses) {
    if (!address || queued.has(address)) continue;
    if (index.domains.get(domainKey(address)) === true) continue;
    if (index.emails.has(address)) continue;
    queued.add(address);
    out.push(address);
  }
  return out;
};

/**
 * Folds a `checkBlacklistBatch` result into a new index.
 *
 * The server answers with the entries that DO exist, so every requested
 * address that is absent from the result is now known to be clean — recording
 * that is what stops us from asking again on the next render.
 *
 * @param {object} index    current index (not mutated)
 * @param {string[]} requested addresses this lookup covered
 * @param {{ emails: Set<string>, domains: Set<string> }} result
 */
export const mergeLookupResult = (index, requested, result) => {
  const blockedEmails = result?.emails || new Set();
  const blockedDomains = result?.domains || new Set();
  const emails = new Map(index.emails);
  const domains = new Map(index.domains);

  for (const address of requested) {
    if (!address) continue;
    emails.set(address, blockedEmails.has(address));
    const domain = domainKey(address);
    // Only domains derived from the requested addresses were checked, so only
    // those may be recorded.
    if (domain) domains.set(domain, blockedDomains.has(domain));
  }

  return { emails, domains };
};

/**
 * Records a block we just performed ourselves, without a round trip.
 *
 * `entry` is the canonical blacklist doc id written by useBlockContact —
 * "@acme.com" for a domain block, "jane@acme.com" for a single address.
 */
export const markBlocked = (index, entry) => {
  const value = (entry || '').toString().trim().toLowerCase();
  if (!value) return index;
  const emails = new Map(index.emails);
  const domains = new Map(index.domains);
  if (value.startsWith('@')) domains.set(value, true);
  else emails.set(value, true);
  return { emails, domains };
};

/** Synchronous answer for the row. Unknown addresses count as not blocked. */
export const isAddressBlocked = (address, index) => {
  if (!address || !index) return false;
  if (index.emails.get(address) === true) return true;
  const domain = domainKey(address);
  return !!domain && index.domains.get(domain) === true;
};
